import { useGSAP } from '@gsap/react';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaMapMarkerAlt, FaPhoneAlt } from 'react-icons/fa';
import { ALL_LANGS } from '../../../constants';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { ModuleBgColor } from '../../../templates/Page';
import { localize } from '../../../utils/sanity';
import { clsx, replaceNewLinesWithBr } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import SmartLink from '../SmartLink';
import * as styles from './ContactsModule.module.scss';
import TextWithBackgroundImageModule from './TextWithBackgroundImageModule';
import { BREAKPOINTS } from '../../../styles/breakpoints';

export const ContactsModuleFragment = graphql`
  fragment ContactsModule on SanityPageContactsModule {
    contacts
  }
`;

export interface ContactsModuleProps {
  className?: string;
}

interface QueryData {
  sanityWebsite: {
    contacts: {
      address: string;
      mobileMapImage: SanityImageType;
      tabletMapImage: SanityImageType;
      desktopMapImage: SanityImageType;
      email: string;
      phoneNumbers: Array<string>;
      whatsAppNumber?: string;
      calendlyLink: string;
    };
  };
}

export function getModuleBgColor(props: ContactsModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'colored';
}

function ContactsModule(props: ContactsModuleProps & CommonModuleProps): React.ReactElement {
  const queryData = useStaticQuery<QueryData>(graphql`
    {
      sanityWebsite {
        contacts {
          address {
            ...LocaleTextFragment
          }
          mobileMapImage {
            ...SanityImage
          }
          tabletMapImage {
            ...SanityImage
          }
          desktopMapImage {
            ...SanityImage
          }
          email
          phoneNumbers
          whatsAppNumber
          calendlyLink
        }
      }
    }
  `);

  const { className, moduleId } = props;

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: QueryData = localize(queryData, langs);

  const {
    sanityWebsite: { contacts },
  } = localizedData;

  const containerRef = useRef<HTMLElement | null>(null);

  useGSAP(() => {}, { scope: containerRef });

  return (
    <TextWithBackgroundImageModule
      moduleId={moduleId}
      mobileImage={localizedData.sanityWebsite.contacts.mobileMapImage}
      tabletImage={localizedData.sanityWebsite.contacts.tabletMapImage}
      desktopImage={localizedData.sanityWebsite.contacts.desktopMapImage}
      imagePosition="left"
      extraImageProps={{
        dimensions: [
          [1000, 1800],
          [
            BREAKPOINTS.tabletVertical,
            localizedData.sanityWebsite.contacts.tabletMapImage,
            960,
            700,
          ],
          [BREAKPOINTS.laptop, localizedData.sanityWebsite.contacts.desktopMapImage, 2880, 1920],
        ],
        queryParams: { fm: 'png' },
      }}
      textBackgroundColor="#E5F8E8"
      contentClassName={styles.container}
      withBoxShadow
      withNarrowTextBox
      withoutImageParallax
    >
      <div className={styles.contactsContainer}>
        <div className={clsx(styles.contactContainer, styles.address)}>
          <div className={styles.titleContainer}>
            <FaMapMarkerAlt className={styles.icon} />
            <h2 className={styles.title}>{t('contacts-page.address', 'Morada')}</h2>
          </div>
          <SmartLink
            className={styles.contact}
            to={'https://www.google.com/maps/place/' + encodeURI(contacts.address)}
          >
            {replaceNewLinesWithBr(contacts.address)}
          </SmartLink>
        </div>
        <div className={clsx(styles.contactContainer, styles.email)}>
          <div className={styles.titleContainer}>
            <FaEnvelope className={styles.icon} />
            <h2 className={styles.title}>{t('contacts-page.email', 'Email')}</h2>
          </div>
          <SmartLink className={styles.contact} to={'mailto:' + contacts.email}>
            {contacts.email}
          </SmartLink>
        </div>
        <div className={clsx(styles.contactContainer, styles.phone)}>
          <div className={styles.titleContainer}>
            <FaPhoneAlt className={styles.icon} />
            <h2 className={styles.title}>
              {t('contacts-page.phoneNumbers', 'Contactos telefónicos')}
            </h2>
          </div>
          {contacts.phoneNumbers.map((phoneNumber, i) => (
            <SmartLink className={styles.contact} key={i} to={'tel:' + phoneNumber}>
              {phoneNumber}
            </SmartLink>
          ))}
        </div>
      </div>
    </TextWithBackgroundImageModule>
  );
}

export default ContactsModule;
